import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../auth/AuthContext';
import ComplianceScannerReport from './ComplianceScannerReport';
import Contractors from './Contractors';
import './Onboarding.css';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API_KEY = ''; // Step 41 — auth via httpOnly cookie; this stub keeps existing 'Bearer ' headers harmless

const SUBSCRIPTION_TIERS = [
  { id: 'professional', name: 'Professional', price: '£649/mo', features: ['Up to 50 properties', 'Email support', 'Basic reporting', '⚠️ Section 8 automation available on Agency Pro only'] },
  { id: 'agency_pro', name: 'Agency Pro', price: '£849/mo', features: ['Unlimited properties', 'Section 8 automation', 'Priority support', 'Multi-user accounts', 'Advanced reporting'] }
];

const TONE_OPTIONS = ['Formal', 'Professional', 'Friendly'];
// 2026-02 (Item 2) — `supervised` mode dropped. It was functionally
// identical to `full` (send immediately unless escalation) so the
// label misled operators into thinking oversight was in place. Only
// two real modes remain: draft-and-approve (queue, review, approve)
// and full-autonomy (auto-send routine comms, still escalate legal
// / sensitive via E1-E8). Default is draft-and-approve.
const AUTONOMY_MODES = [
  { id: 'draft_and_approve', name: 'Draft and Approve', description: 'All emails held for approval before sending (Recommended)', default: true },
  { id: 'full', name: 'Full Autonomy', description: 'AI sends routine emails without human review; legal/sensitive still queued', warning: true },
];

function Onboarding({ onComplete }) {
  const { user } = useAuth();
  const isAuthenticated = !!user;
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);
  
  // Step 1: Agency Details
  const [agencyData, setAgencyData] = useState({
    companyName: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    subscriptionTier: 'professional',
    // 2026-02 — Structured company address (correspondence address used
    // on serviced notices). Default country is United Kingdom.
    addressLine1: '',
    addressLine2: '',
    city: '',
    postcode: '',
    country: 'United Kingdom',
  });

  // Step 45 bug-fix — when an already-authenticated agent opens the Onboarding
  // wizard from the sidebar, pre-fill Step 1 from their AgentClient record so
  // they don't re-enter account data they already provided at signup. The
  // subscription-tier picker is hidden for authenticated users (see below) —
  // they already chose a tier when they signed up.
  useEffect(() => {
    if (!user) return;
    setAgencyData(prev => ({
      ...prev,
      companyName: prev.companyName || user.company_name || '',
      contactName: prev.contactName || user.contact_name || '',
      contactEmail: prev.contactEmail || user.contact_email || '',
    }));
    // 2026-02 — Pre-fill the structured address from the agency profile
    // so a re-entry to the wizard shows what's already saved.
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/agency/profile`, { credentials: 'include' });
        if (!res.ok) return;
        const p = await res.json();
        setAgencyData(prev => ({
          ...prev,
          contactPhone: prev.contactPhone || p.contact_phone || '',
          addressLine1: prev.addressLine1 || p.address_line1 || '',
          addressLine2: prev.addressLine2 || p.address_line2 || '',
          city: prev.city || p.city || '',
          postcode: prev.postcode || p.postcode || '',
          country: prev.country || p.country || 'United Kingdom',
        }));
      } catch { /* non-fatal — wizard still works with blank fields */ }
    })();
  }, [user]);
  
  // Step 2: Email Preferences
  const [emailPrefs, setEmailPrefs] = useState({
    senderName: '',
    tone: 'Professional',
    signoffPhrase: '',
    htmlSignature: '',
    autonomyMode: 'draft_and_approve',
    showFullAutonomyWarning: false
  });
  
  // Step 3: Import Data
  const [importData, setImportData] = useState({
    selectedPath: null,
    csvFile: null,
    csvData: [],
    columnMapping: {},
    templateFile: null,
    pdfFiles: [],
    extractedData: []
  });
  
  // Step 4: Review Data
  const [reviewData, setReviewData] = useState({
    records: [],
    duplicates: [],
    resolvedDuplicates: new Set(),
    hasScrolledToBottom: false,
    editingCell: null
  });
  
  // Step 5: Compliance
  const [complianceData, setComplianceData] = useState({
    properties: [],
    documents: {}
  });
  
  // Step 6: Summary
  const [summary, setSummary] = useState({
    tenantsImported: 0,
    propertiesImported: 0,
    complianceDocsUploaded: 0,
    outstandingItems: []
  });

  // Compliance Scanner state — populated when advancing past the
  // compliance upload step; rendered on the completion step.
  const [complianceScans, setComplianceScans] = useState(null);
  const [complianceSummary, setComplianceSummary] = useState(null);
  const [scanningCompliance, setScanningCompliance] = useState(false);

  const runComplianceScan = async () => {
    setScanningCompliance(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/compliance/scanner/portfolio?trigger=onboarding`,
        { method: 'POST', credentials: 'include' }
      );
      if (!res.ok) return;
      const data = await res.json();
      setComplianceScans(data.scans || []);
      setComplianceSummary(data.summary || null);
    } catch (e) {
      console.warn('compliance scan failed', e);
    } finally {
      setScanningCompliance(false);
    }
  };

  const refreshComplianceScans = async () => {
    // Re-fetch latest scan per property so override/NA reflect immediately.
    if (!complianceScans) return;
    const updated = [];
    for (const s of complianceScans) {
      try {
        const r = await fetch(
          `${API_BASE_URL}/api/compliance/scanner/latest/property/${s.property_id}`,
          { credentials: 'include' });
        if (r.ok) {
          const d = await r.json();
          if (d.scan) updated.push(d.scan);
        }
      } catch {}
    }
    if (updated.length) setComplianceScans(updated);
  };
  
  const reviewTableRef = useRef(null);
  
  // Validate step before proceeding
  const validateStep = (step) => {
    switch(step) {
      case 1:
        // 2026-02 — Step 1 now requires the structured company address
        // too. Address line 1, city and postcode are mandatory; line 2
        // and country (which defaults to United Kingdom) are optional.
        return agencyData.companyName && agencyData.contactName &&
               agencyData.contactEmail && agencyData.contactPhone &&
               agencyData.addressLine1 && agencyData.city &&
               agencyData.postcode;
      case 2:
        return emailPrefs.senderName && emailPrefs.signoffPhrase;
      case 3:
        return reviewData.records.length > 0;
      case 4:
        const allDuplicatesResolved = reviewData.duplicates.every(dup => 
          reviewData.resolvedDuplicates.has(dup.id)
        );
        const allRequiredFieldsFilled = reviewData.records.every(record => 
          record.firstName && record.lastName && record.address && record.rent
        );
        // Only require scroll to bottom if there are more than 5 records (table is long enough to scroll)
        const scrollRequirementMet = reviewData.records.length <= 5 || reviewData.hasScrolledToBottom;
        return allDuplicatesResolved && allRequiredFieldsFilled && scrollRequirementMet;
      case 5:
        return true; // Optional step
      default:
        return true;
    }
  };
  
  const [agencySaving, setAgencySaving] = useState(false);
  const [conciergeSubmitting, setConciergeSubmitting] = useState(false);

  // 2026-02 — Persist Step 1 (company address) to the existing
  // /api/agency/profile endpoint when the agent advances past Step 1.
  // Returns true on success / no-op, false on failure (the wizard
  // shows an alert and stays on Step 1).
  const saveAgencyProfile = async () => {
    if (!user) return true; // unauth signup flow saves elsewhere
    setAgencySaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/agency/profile`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_name: agencyData.companyName,
          contact_name: agencyData.contactName,
          contact_email: agencyData.contactEmail,
          contact_phone: agencyData.contactPhone,
          address_line1: agencyData.addressLine1,
          address_line2: agencyData.addressLine2 || null,
          city: agencyData.city,
          county: null,
          postcode: agencyData.postcode,
          country: agencyData.country || 'United Kingdom',
        }),
      });
      if (!res.ok) {
        const t = await res.text();
        alert('Could not save agency details: ' + (t || `HTTP ${res.status}`));
        return false;
      }
      return true;
    } catch (err) {
      alert('Could not save agency details: ' + err.message);
      return false;
    } finally {
      setAgencySaving(false);
    }
  };

  // 2026-02 — Concierge skip. Records the flag on the AgentClient and
  // routes the agent directly into the live dashboard, which will show
  // the amber "Onboarding in progress — your concierge is setting up
  // your account." banner. Available from Step 2 onwards (Step 1 must
  // be completed first so the concierge has the agency's contact
  // details).
  const requestConciergeOnboarding = async () => {
    if (!validateStep(1)) {
      alert('Please complete Step 1 (Agency Details) before requesting concierge onboarding.');
      return;
    }
    if (!window.confirm(
      'Skip the rest of the setup wizard and hand off to the Estatel AI concierge team?\n\n' +
      'You will go straight to the dashboard. Your concierge will finish setting up your account in the background.'
    )) return;
    setConciergeSubmitting(true);
    try {
      // Make sure the latest Step 1 details are saved before handoff.
      const ok = await saveAgencyProfile();
      if (!ok) { setConciergeSubmitting(false); return; }
      const res = await fetch(`${API_BASE_URL}/api/agency/concierge-request`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        const t = await res.text();
        alert('Could not request concierge onboarding: ' + (t || `HTTP ${res.status}`));
        setConciergeSubmitting(false);
        return;
      }
      if (onComplete) onComplete();
      window.location.href = '/';
    } catch (err) {
      alert('Could not request concierge onboarding: ' + err.message);
      setConciergeSubmitting(false);
    }
  };

  const nextStep = () => {
    if (!validateStep(currentStep)) {
      alert('Please complete all required fields before proceeding.');
      return;
    }
    // 2026-02 — Persist Step 1 details to /api/agency/profile before
    // advancing. Concierge gating downstream relies on these being on
    // the AgentClient document.
    if (currentStep === 1) {
      saveAgencyProfile().then(ok => {
        if (!ok) return;
        logAuditTrail(1, 'completed');
        setCompletedSteps([...completedSteps, 1]);
        setCurrentStep(2);
      });
      return;
    }
    logAuditTrail(currentStep, 'completed');
    setCompletedSteps([...completedSteps, currentStep]);
    // 2026-05-29 — After compliance upload step, kick off a portfolio
    // compliance scan so the agent sees a traffic-light health report
    // on the Setup-Complete screen. Non-blocking — the wizard advances
    // immediately and the scan resolves async.
    if (currentStep === 5) {
      runComplianceScan();
    }
    setCurrentStep(currentStep + 1);
  };
  
  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  const logAuditTrail = (step, action) => {
    console.log(`[AUDIT] Step ${step} ${action} at ${new Date().toISOString()} by ${agencyData.contactName || 'Agent'}`);
    // In production, send to backend audit log API
  };

  // Path B (FIX 1) — download the Estatel AI pre-formatted import template.
  // Path A (FIX 2) — download the test CRM CSV (for column-mapping demo).
  // Both come from authenticated backend endpoints; we use a fetch +
  // blob round-trip so the httpOnly session cookie travels with the
  // request (a plain <a download> would break for cookie auth).
  const downloadFile = async (url, filename) => {
    try {
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw new Error(`Download failed (HTTP ${res.status})`);
      const blob = await res.blob();
      const dlUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = dlUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(dlUrl);
    } catch (err) {
      window.alert('Download failed: ' + err.message);
    }
  };

  const downloadImportTemplate = () =>
    downloadFile(
      `${API_BASE_URL}/api/onboarding/template/import-xlsx`,
      'estateiq_import_template.xlsx'
    );

  const downloadTestCsv = () =>
    downloadFile(
      `${API_BASE_URL}/api/onboarding/template/test-csv`,
      'test_crm_export.csv'
    );

  // Path C (FIX 3) — POST the uploaded tenancy-agreement PDFs (or
  // scanned PDFs or PNG/JPG images) to the backend's Claude-powered
  // extractor. Returns tenant + landlord + property + tenancy details.
  // Each landlord goes through the auto-match-vs-create-new flow on
  // Step 4 (review).
  //
  // FIX 2 (body-stream workaround) — Cloudflare occasionally throws
  // "Failed to execute json on Response: body stream already read"
  // when response body is consumed after an automatic retry. Workaround:
  // read the body as text once, then JSON.parse it ourselves. Do NOT
  // call res.json() anywhere in this flow.
  const [extractingPdfs, setExtractingPdfs] = useState(false);
  const extractPdfs = async (files) => {
    setExtractingPdfs(true);
    const results = [];
    for (const f of files) {
      const fd = new FormData();
      fd.append('file', f);
      try {
        const res = await fetch(`${API_BASE_URL}/api/onboarding/extract-tenancy-pdf`, {
          method: 'POST', credentials: 'include', body: fd,
        });
        const text = await res.text();
        let data;
        try { data = JSON.parse(text); }
        catch { data = { detail: text || 'Invalid JSON response' }; }
        if (res.ok) {
          results.push({
            filename: f.name,
            ok: true,
            route: data.route,
            pending_pdf_id: data.pending_pdf_id,
            ...data.extracted,
          });
        } else {
          results.push({ filename: f.name, ok: false,
                         error: data.detail || `HTTP ${res.status}` });
        }
      } catch (err) {
        results.push({ filename: f.name, ok: false, error: err.message });
      }
    }
    // FIX 1/5 — map successful extractions into reviewData.records so the
    // Step 3 → Step 4 "Next" button de-ghosts. validateStep(3) requires
    // reviewData.records.length > 0.
    const reviewRecords = results
      .filter(r => r.ok && r.tenant)
      .map(r => {
        const addressParts = [
          r.property?.address_line1, r.property?.address_line2,
          r.property?.city, r.property?.postcode,
        ].filter(Boolean).map(x => String(x).trim());
        return {
          firstName: r.tenant.first_name || '',
          lastName: r.tenant.last_name || '',
          email: r.tenant.email || '',
          phone: normaliseUKPhoneJS(r.tenant.phone || ''),
          address: addressParts.join(', '),
          rent: r.tenancy?.rent_amount != null ? String(r.tenancy.rent_amount) : '',
          // TASK 5 — propagate the temporary PDF id and address parts so
          // /tenants/import can attach the stored PDF to the new Tenancy.
          pending_pdf_id: r.pending_pdf_id || null,
          address_line1: r.property?.address_line1 || '',
          address_line2: r.property?.address_line2 || '',
          city: r.property?.city || '',
          postcode: r.property?.postcode || '',
          rent_frequency: r.tenancy?.rent_frequency || null,
          tenancy_start_date: r.tenancy?.start_date || null,
          tenancy_end_date: r.tenancy?.end_date || null,
          id: Math.random().toString(36).substr(2, 9),
        };
      });
    setImportData(prev => ({ ...prev, pdfFiles: files, extractedData: results,
                             selectedPath: 'pdf' }));
    if (reviewRecords.length > 0) {
      setReviewData(prev => ({ ...prev, records: [...prev.records, ...reviewRecords] }));
    }
    setExtractingPdfs(false);
  };
  
  // Step 3: Parse CSV
  const parseCSV = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split('\n').filter(line => line.trim());
      const headers = lines[0].split(',').map(h => h.trim());
      const rows = lines.slice(1).map(line => {
        const values = line.split(',').map(v => v.trim());
        return headers.reduce((obj, header, index) => {
          obj[header] = values[index] || '';
          return obj;
        }, {});
      });
      setImportData(prev => ({ ...prev, csvData: rows, csvFile: file, columnMapping: {} }));
      // 2026-02 — Kick the AI auto-mapping in the background so the
      // dropdowns are pre-filled by the time the agent looks at them.
      suggestColumnMapping(headers, rows.slice(0, 3));
    };
    reader.readAsText(file);
  };

  // 2026-02 — AI hybrid column-mapping. Calls
  // POST /api/onboarding/suggest-column-mapping with the uploaded
  // headers + first 3 sample rows. Pre-fills the dropdowns and records
  // per-field confidence so the UI can show a small pill.
  const [mappingConfidence, setMappingConfidence] = useState({}); // {field: 'high'|'medium'|'low'}
  const [mappingLoading, setMappingLoading] = useState(false);
  const suggestColumnMapping = async (headers, sampleRows) => {
    if (!headers || headers.length === 0) return;
    setMappingLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/onboarding/suggest-column-mapping`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ headers, sample_rows: sampleRows || [] }),
      });
      if (!res.ok) return;
      const data = await res.json();
      const m = data.mapping || {};
      const cm = {};
      const cf = {};
      for (const field of ['firstName','lastName','email','phone','address','rent',
                            'landlordName','landlordType','landlordEmail','landlordPhone']) {
        if (m[field] && m[field].column) {
          cm[field] = m[field].column;
          cf[field] = m[field].confidence || 'low';
        } else if (m[field]) {
          // Field present in response but unmapped — still record the
          // low-confidence pill so the agent sees "Not found in upload".
          cf[field] = 'low';
        }
      }
      if (Object.keys(cm).length > 0) {
        setImportData(prev => ({ ...prev, columnMapping: { ...prev.columnMapping, ...cm } }));
        setMappingConfidence(cf);
      }
    } catch {
      // Non-fatal — dropdowns stay empty and agent picks manually.
    } finally {
      setMappingLoading(false);
    }
  };
  
  // FIX 1 — Path B template upload handler. Originally hardwired to the
  // Estatel AI-shipped Excel template, which broke when agents uploaded
  // CRM-exported XLSX with different column names. 2026-02 — now detects
  // template-vs-generic by sniffing for the "First Name" column, and
  // falls back to the same AI column-mapping path as CSV when generic.
  const parseTemplateXlsx = async (file) => {
    try {
      const XLSX = await import('xlsx');
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });
      // Drop the red "Delete this example row before uploading" instruction
      // row (its First Name column starts with "Delete this example").
      const cleaned = rows.filter(r => {
        const fn = String(r['First Name'] || '').trim().toLowerCase();
        // For the official template: drop example row. For generic XLSX:
        // no First Name column → cleaned will be the full set anyway.
        return !fn.startsWith('delete this');
      });
      if (cleaned.length === 0) {
        window.alert('No data rows found in the spreadsheet.');
        return;
      }
      const headers = Object.keys(cleaned[0] || {});
      const isOfficialTemplate = headers.includes('First Name')
        && headers.includes('Last Name')
        && headers.includes('Property Address Line 1');

      if (isOfficialTemplate) {
        // Direct mapping (no AI needed) — same as before.
        const mapped = cleaned.map(r => {
          const addressParts = [
            r['Property Address Line 1'], r['Property Address Line 2'],
            r['City'], r['Postcode'],
          ].filter(Boolean).map(x => String(x).trim());
          return {
            firstName: String(r['First Name'] || '').trim(),
            lastName: String(r['Last Name'] || '').trim(),
            email: String(r['Email'] || '').trim(),
            phone: normaliseUKPhoneJS(String(r['Phone Number'] || '').trim()),
            address: addressParts.join(', '),
            rent: String(r['Rent Amount'] || '').trim(),
            id: Math.random().toString(36).substr(2, 9),
          };
        });
        setReviewData(prev => ({ ...prev, records: [...prev.records, ...mapped] }));
        setImportData(prev => ({ ...prev, selectedPath: 'template' }));
        return;
      }

      // 2026-02 — Generic XLSX from a CRM. Route through the same AI
      // column-mapping flow as CSV: present headers + dropdowns,
      // pre-filled by Claude's suggestions, and let the agent confirm.
      setImportData(prev => ({
        ...prev,
        csvData: cleaned,
        csvFile: file,
        columnMapping: {},
        selectedPath: 'xlsx',
      }));
      suggestColumnMapping(headers, cleaned.slice(0, 3));
    } catch (err) {
      window.alert('Could not read the file: ' + err.message);
    }
  };

  // Step 3: Apply column mapping and create records
  //
  // Button wording ("Apply Mapping & Continue to Review") already promises
  // to advance the wizard — so in addition to populating reviewData.records
  // we also flip the step indicator to Step 4. (Previously the agent had
  // to press this button AND the Next button at the bottom, which was a
  // double-click that left many agents stuck.)
  const applyColumnMapping = () => {
    const { csvData, columnMapping } = importData;
    const mapped = csvData.map(row => ({
      firstName: row[columnMapping.firstName] || '',
      lastName: row[columnMapping.lastName] || '',
      email: row[columnMapping.email] || '',
      phone: normaliseUKPhoneJS(row[columnMapping.phone] || ''),
      address: row[columnMapping.address] || '',
      rent: row[columnMapping.rent] || '',
      // 2026-06-04 — Landlord fields carried through to the import API
      // so the backend can match-or-create a Landlord and link it to
      // the imported Property.
      landlord_name: row[columnMapping.landlordName] || '',
      landlord_company_name: row[columnMapping.landlordCompanyName] || '',
      landlord_type: row[columnMapping.landlordType] || '',
      landlord_address_line1: row[columnMapping.landlordAddressLine1] || '',
      landlord_address_line2: row[columnMapping.landlordAddressLine2] || '',
      landlord_city: row[columnMapping.landlordCity] || '',
      landlord_postcode: row[columnMapping.landlordPostcode] || '',
      landlord_email: row[columnMapping.landlordEmail] || '',
      landlord_phone: row[columnMapping.landlordPhone] || '',
      id: Math.random().toString(36).substr(2, 9)
    }));

    setReviewData(prev => ({
      ...prev,
      records: [...prev.records, ...mapped]
    }));
    // Advance directly to Step 4 — Review and Confirm.
    setCurrentStep(4);
  };
  
  // Client-side UK phone normalisation (mirrors backend normaliseUKPhone in utils.py)
  // Applied at import time so agents see normalised numbers in the review table.
  const normaliseUKPhoneJS = (phone) => {
    if (!phone) return phone;
    // Remove all non-digit characters except +
    let cleaned = phone.replace(/[^\d+]/g, '');
    if (cleaned.startsWith('+44')) return cleaned;
    if (cleaned.startsWith('0044')) return '+' + cleaned.substring(2);
    if (cleaned.startsWith('44') && cleaned.length >= 12) return '+' + cleaned;
    if (cleaned.startsWith('0')) return '+44' + cleaned.substring(1);
    return '+44' + cleaned;
  };
  
  // Step 4: Detect duplicates
  useEffect(() => {
    if (reviewData.records.length > 0) {
      const duplicates = [];
      const seen = new Map();
      
      reviewData.records.forEach((record, index) => {
        const key = `${record.phone}-${record.email}`.toLowerCase();
        if (seen.has(key)) {
          const duplicateId = `dup-${index}`;
          duplicates.push({
            id: duplicateId,
            indices: [seen.get(key), index],
            records: [reviewData.records[seen.get(key)], record]
          });
        } else {
          seen.set(key, index);
        }
      });
      
      setReviewData(prev => ({ ...prev, duplicates }));
    }
  }, [reviewData.records.length]);
  
  // Step 4: Scroll detection
  useEffect(() => {
    const handleScroll = () => {
      if (reviewTableRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = reviewTableRef.current;
        if (scrollTop + clientHeight >= scrollHeight - 10) {
          setReviewData(prev => ({ ...prev, hasScrolledToBottom: true }));
        }
      }
    };
    
    const tableElement = reviewTableRef.current;
    if (tableElement) {
      tableElement.addEventListener('scroll', handleScroll);
      return () => tableElement.removeEventListener('scroll', handleScroll);
    }
  }, [currentStep]);
  
  // Step 4: Edit cell — normalise phone on blur
  const handleCellEdit = (recordId, field, value) => {
    setReviewData(prev => ({
      ...prev,
      records: prev.records.map(record => 
        record.id === recordId ? { ...record, [field]: value } : record
      )
    }));
  };
  
  const handlePhoneBlur = (recordId, value) => {
    const normalized = normaliseUKPhoneJS(value);
    if (normalized !== value) {
      handleCellEdit(recordId, 'phone', normalized);
    }
  };
  
  // Step 4: Resolve duplicate
  const resolveDuplicate = (duplicateId, action, keepIndex) => {
    const duplicate = reviewData.duplicates.find(d => d.id === duplicateId);
    if (!duplicate) return;
    
    if (action === 'keep_both') {
      // Just mark as resolved
    } else if (action === 'delete_one') {
      const deleteIndex = keepIndex === 0 ? 1 : 0;
      const recordToDelete = duplicate.records[deleteIndex];
      setReviewData(prev => ({
        ...prev,
        records: prev.records.filter(r => r.id !== recordToDelete.id)
      }));
    }
    
    setReviewData(prev => ({
      ...prev,
      resolvedDuplicates: new Set([...prev.resolvedDuplicates, duplicateId])
    }));
  };
  
  // Step 4: Confirm and create records
  const confirmAndGoLive = async () => {
    try {
      logAuditTrail(4, 'confirmed - going live with data');
      
      // POST to backend to create all records (normalisation applied server-side)
      const headers = {
        'Authorization': `Bearer ${API_KEY}`,
        'Content-Type': 'application/json'
      };
      
      const tenants = reviewData.records.map(r => ({
        first_name: r.firstName,
        last_name: r.lastName,
        email: r.email || null,
        phone: r.phone,
        address: r.address,
        rent: r.rent,
        // TASK 5 — pass through Path C metadata so the backend can
        // create the matching Property + Tenancy and attach the
        // stored tenancy-agreement PDF.
        pending_pdf_id: r.pending_pdf_id || null,
        address_line1: r.address_line1 || null,
        address_line2: r.address_line2 || null,
        city: r.city || null,
        postcode: r.postcode || null,
        rent_frequency: r.rent_frequency || null,
        tenancy_start_date: r.tenancy_start_date || null,
        tenancy_end_date: r.tenancy_end_date || null,
        // 2026-06-04 — Landlord fields. Backend will match-or-create
        // a Landlord record (dedupe by name) and link it to the
        // imported Property.
        landlord_name: r.landlord_name || null,
        landlord_company_name: r.landlord_company_name || null,
        landlord_type: r.landlord_type || null,
        landlord_address_line1: r.landlord_address_line1 || null,
        landlord_address_line2: r.landlord_address_line2 || null,
        landlord_city: r.landlord_city || null,
        landlord_postcode: r.landlord_postcode || null,
        landlord_email: r.landlord_email || null,
        landlord_phone: r.landlord_phone || null,
      }));
      
      const response = await fetch(`${API_BASE_URL}/api/tenants/import`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ tenants })
      });
      
      if (!response.ok) {
        console.error('Import API failed, continuing with local data');
      } else {
        const result = await response.json();
        console.log('Import result:', result);
      }
      
      // Prepare properties from unique addresses
      const uniqueAddresses = [...new Set(reviewData.records.map(r => r.address))];
      setComplianceData(prev => ({
        ...prev,
        properties: uniqueAddresses.map(addr => ({ address: addr, documents: {} }))
      }));
      
      // Update summary
      setSummary({
        tenantsImported: reviewData.records.length,
        propertiesImported: uniqueAddresses.length,
        complianceDocsUploaded: 0,
        outstandingItems: uniqueAddresses.map(addr => `Missing compliance docs for ${addr}`)
      });
      
      nextStep();
    } catch (error) {
      alert('Error creating records: ' + error.message);
    }
  };
  
  const renderStepIndicator = () => {
    const steps = [
      'Agency Details',
      'Email Preferences',
      'Import Data',
      'Review & Confirm',
      'Compliance',
      'Contractors',
      'Pre-Tenancy',
      'Complete'
    ];
    
    return (
      <div className="step-indicator">
        {steps.map((step, index) => (
          <div key={index} className="step-indicator-item">
            <div className={`step-number ${currentStep === index + 1 ? 'active' : ''} ${completedSteps.includes(index + 1) ? 'completed' : ''}`}>
              {completedSteps.includes(index + 1) ? '✓' : index + 1}
            </div>
            <div className="step-label">{step}</div>
          </div>
        ))}
      </div>
    );
  };
  
  const renderStep1 = () => (
    <div className="wizard-step">
      <h2>Agency Details</h2>
      <p className="step-description">Let's start with your agency information</p>
      
      <div className="form-grid">
        <div className="form-group">
          <label>Company Name *</label>
          <input
            type="text"
            value={agencyData.companyName}
            onChange={(e) => setAgencyData({...agencyData, companyName: e.target.value})}
            placeholder="Acme Lettings Ltd"
            data-testid="company-name"
          />
        </div>
        
        <div className="form-group">
          <label>Contact Name *</label>
          <input
            type="text"
            value={agencyData.contactName}
            onChange={(e) => setAgencyData({...agencyData, contactName: e.target.value})}
            placeholder="James Smith"
            data-testid="contact-name"
          />
        </div>
        
        <div className="form-group">
          <label>Contact Email *</label>
          <input
            type="email"
            value={agencyData.contactEmail}
            onChange={(e) => setAgencyData({...agencyData, contactEmail: e.target.value})}
            placeholder="james@acmelettings.co.uk"
            data-testid="contact-email"
          />
        </div>
        
        <div className="form-group">
          <label>Contact Phone *</label>
          <input
            type="tel"
            value={agencyData.contactPhone}
            onChange={(e) => setAgencyData({...agencyData, contactPhone: e.target.value})}
            placeholder="+44 20 1234 5678"
            data-testid="contact-phone"
          />
        </div>
      </div>

      {/* 2026-02 — Structured company correspondence address. Used as
          the serviced-notice address on Form 3A §5.5 and any agent-
          issued paperwork. Mandatory: address_line1, city, postcode.
          Country defaults to United Kingdom. */}
      <h3 style={{ marginTop: '1.25rem' }}>Company Address</h3>
      <div className="form-grid">
        <div className="form-group" style={{ gridColumn: '1 / -1' }}>
          <label>Address Line 1 *</label>
          <input
            type="text"
            value={agencyData.addressLine1}
            onChange={(e) => setAgencyData({...agencyData, addressLine1: e.target.value})}
            placeholder="12 High Street"
            data-testid="company-address-line1"
          />
        </div>
        <div className="form-group" style={{ gridColumn: '1 / -1' }}>
          <label>Address Line 2</label>
          <input
            type="text"
            value={agencyData.addressLine2}
            onChange={(e) => setAgencyData({...agencyData, addressLine2: e.target.value})}
            placeholder="Suite 4 (optional)"
            data-testid="company-address-line2"
          />
        </div>
        <div className="form-group">
          <label>City *</label>
          <input
            type="text"
            value={agencyData.city}
            onChange={(e) => setAgencyData({...agencyData, city: e.target.value})}
            placeholder="London"
            data-testid="company-city"
          />
        </div>
        <div className="form-group">
          <label>Postcode *</label>
          <input
            type="text"
            value={agencyData.postcode}
            onChange={(e) => setAgencyData({...agencyData, postcode: e.target.value.toUpperCase()})}
            placeholder="SW1A 1AA"
            data-testid="company-postcode"
          />
        </div>
        <div className="form-group">
          <label>Country</label>
          <input
            type="text"
            value={agencyData.country}
            onChange={(e) => setAgencyData({...agencyData, country: e.target.value})}
            placeholder="United Kingdom"
            data-testid="company-country"
          />
        </div>
      </div>
      
      <div className="subscription-tiers" style={isAuthenticated ? { display: 'none' } : {}} data-testid="onboarding-subscription-tiers">
        <h3>Choose Your Subscription Tier</h3>
        <div className="tier-cards">
          {SUBSCRIPTION_TIERS.map(tier => (
            <div
              key={tier.id}
              className={`tier-card ${agencyData.subscriptionTier === tier.id ? 'selected' : ''}`}
              onClick={() => setAgencyData({...agencyData, subscriptionTier: tier.id})}
              data-testid={`tier-${tier.id}`}
            >
              <h4>{tier.name}</h4>
              <div className="tier-price">{tier.price}</div>
              <ul className="tier-features">
                {tier.features.map((feature, idx) => (
                  <li key={idx}>✓ {feature}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
  
  const renderStep2 = () => (
    <div className="wizard-step">
      <h2>Email Preferences</h2>
      <p className="step-description">Configure how Estatel AI sends emails on your behalf</p>
      
      <div className="form-grid">
        <div className="form-group">
          <label>Sender Display Name *</label>
          <input
            type="text"
            value={emailPrefs.senderName}
            onChange={(e) => setEmailPrefs({...emailPrefs, senderName: e.target.value})}
            placeholder="James at Acme Lettings"
            data-testid="sender-name"
          />
          <small>This is the name tenants see in the From field</small>
        </div>
        
        <div className="form-group">
          <label>Tone Preference</label>
          <select
            value={emailPrefs.tone}
            onChange={(e) => setEmailPrefs({...emailPrefs, tone: e.target.value})}
            data-testid="tone-select"
          >
            {TONE_OPTIONS.map(tone => (
              <option key={tone} value={tone}>{tone}</option>
            ))}
          </select>
        </div>
        
        <div className="form-group full-width">
          <label>Sign-off Phrase *</label>
          <input
            type="text"
            value={emailPrefs.signoffPhrase}
            onChange={(e) => setEmailPrefs({...emailPrefs, signoffPhrase: e.target.value})}
            placeholder="Kind regards, James at Acme Lettings"
            data-testid="signoff-phrase"
          />
        </div>
        
        <div className="form-group full-width">
          <label>HTML Signature</label>
          <div className="signature-editor">
            <textarea
              value={emailPrefs.htmlSignature}
              onChange={(e) => setEmailPrefs({...emailPrefs, htmlSignature: e.target.value})}
              placeholder="<p>James Smith<br>Acme Lettings Ltd<br>Tel: +44 20 1234 5678</p>"
              rows="6"
              data-testid="html-signature"
            />
            <div className="signature-preview">
              <h4>Live Preview:</h4>
              <div dangerouslySetInnerHTML={{
                __html: emailPrefs.htmlSignature
                  // 2026-06-04 — Plain newlines in the textarea were being
                  // collapsed by the browser when rendered as HTML, making
                  // the agent think Return was being swallowed. Normalise
                  // \n → <br> for the preview (the same normalisation is
                  // applied on save in Settings.js line 788).
                  ? emailPrefs.htmlSignature.replace(/(?<!>)\n/g, '<br>')
                  : '<p>Preview will appear here...</p>'
              }} />
            </div>
          </div>
        </div>
      </div>
      
      <div className="autonomy-mode-section">
        <h3>Email Autonomy Mode</h3>
        <p>Choose how Estatel AI handles email sending</p>
        
        {AUTONOMY_MODES.map(mode => (
          <div key={mode.id} className="autonomy-option">
            <label className="radio-label">
              <input
                type="radio"
                name="autonomyMode"
                value={mode.id}
                checked={emailPrefs.autonomyMode === mode.id}
                onChange={(e) => {
                  if (mode.warning) {
                    setEmailPrefs({...emailPrefs, showFullAutonomyWarning: true});
                  } else {
                    setEmailPrefs({...emailPrefs, autonomyMode: e.target.value, showFullAutonomyWarning: false});
                  }
                }}
                data-testid={`autonomy-${mode.id}`}
              />
              <div>
                <strong>{mode.name}</strong>
                {mode.default && <span className="badge-recommended">Recommended</span>}
                <p>{mode.description}</p>
              </div>
            </label>
          </div>
        ))}
        
        {emailPrefs.showFullAutonomyWarning && (
          <div className="warning-box">
            <strong>⚠️ Warning:</strong> Not recommended. Sensitive and legal emails will be sent without your review. 
            You can change this in Settings at any time.
            <div className="warning-actions">
              <button 
                className="btn-warning"
                onClick={() => setEmailPrefs({...emailPrefs, autonomyMode: 'full', showFullAutonomyWarning: false})}
                data-testid="confirm-full-autonomy"
              >
                I Understand, Enable Full Autonomy
              </button>
              <button 
                className="btn-secondary"
                onClick={() => setEmailPrefs({...emailPrefs, showFullAutonomyWarning: false})}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
  
  const renderStep3 = () => (
    <div className="wizard-step">
      <h2>Choose Portfolio Setup Method</h2>
      <p className="step-description">How would you like to set up your portfolio?</p>

      {/* 2026-02 — Phase 1 rebuild. Three cards only:
          (A) CSV / Excel — combined CSV + XLSX path with AI column-mapping.
          (B) Tenancy Agreements — bulk PDF / image extraction.
          (C) Concierge Onboarding — hand-off to the Estatel AI concierge team.
          Manual add is no longer available during onboarding; it lives
          only on the live dashboard once setup is complete. */}
      <div className="import-paths" data-testid="step3-method-cards">
        {/* Card A: CSV / Excel */}
        <div
          className={`import-path-card ${importData.selectedPath === 'csv' || importData.selectedPath === 'xlsx' ? 'is-selected' : ''}`}
          data-testid="card-csv-excel"
        >
          <h3>Upload CSV / Excel</h3>
          <p className="card-tagline" style={{ color: '#475569', fontSize: '0.9rem' }}>
            Best if you already have a spreadsheet or CRM export. Estatel AI AI will map your columns, identify landlords, properties, tenants and tenancy details, then build your account structure.
          </p>
          <p style={{ fontSize: '0.8rem', color: '#64748b', margin: '0.4rem 0 0.6rem' }}>
            Accepted: CSV, XLS, XLSX
          </p>

          <input
            type="file"
            accept=".csv,.xls,.xlsx"
            onChange={(e) => {
              const f = e.target.files && e.target.files[0];
              if (!f) return;
              const name = f.name.toLowerCase();
              if (name.endsWith('.csv')) {
                parseCSV(f);
                setImportData(prev => ({ ...prev, selectedPath: 'csv' }));
              } else {
                parseTemplateXlsx(f);
                setImportData(prev => ({ ...prev, selectedPath: 'xlsx' }));
              }
            }}
            className="file-input"
            data-testid="csv-excel-upload"
          />

          <div style={{ display: 'flex', gap: '0.9rem', flexWrap: 'wrap', marginTop: '0.6rem' }}>
            <button
              type="button"
              className="btn-link"
              style={{ background: 'none', border: 'none', color: '#0073EA',
                       textDecoration: 'underline', cursor: 'pointer',
                       padding: '0.4rem 0', fontSize: '0.85rem' }}
              onClick={downloadImportTemplate}
              data-testid="download-template">
              Download our pre-formatted Excel template
            </button>
            <button
              type="button"
              className="btn-link"
              style={{ background: 'none', border: 'none', color: '#0073EA',
                       textDecoration: 'underline', cursor: 'pointer',
                       padding: '0.4rem 0', fontSize: '0.85rem' }}
              onClick={downloadTestCsv}
              data-testid="download-test-csv">
              Download a sample CRM CSV to try the column-mapping
            </button>
          </div>

          {importData.csvData.length > 0 && (
            <div className="column-mapping">
              <h4>
                Map Your Columns
                {mappingLoading && (
                  <span style={{ marginLeft: '0.5rem', fontSize: '0.8rem', color: '#0073EA', fontWeight: 400 }}>
                    AI is suggesting matches…
                  </span>
                )}
              </h4>
              <p>
                Estatel AI AI has suggested the most likely matches based on your column names.
                Review the pills next to each row and adjust if needed.
              </p>
              <div className="mapping-grid">
                {[
                  // 2026-06-04 — Field groups. The dropdown row gets a
                  // `__group` header row injected just before the first
                  // item of each group so the agent can clearly see
                  // Tenant / Property / Landlord boundaries.
                  { key: 'firstName',     label: 'Tenant First Name *',  required: true,  group: 'tenant' },
                  { key: 'lastName',      label: 'Tenant Last Name *',   required: true,  group: 'tenant' },
                  { key: 'email',         label: 'Tenant Email',         required: false, group: 'tenant' },
                  { key: 'phone',         label: 'Tenant Phone',         required: false, group: 'tenant' },
                  { key: 'address',       label: 'Property Address *',   required: true,  group: 'property' },
                  { key: 'rent',          label: 'Rent Amount *',        required: true,  group: 'tenancy' },
                  // 2026-06-04 — Full Landlord set (Form 3A §5.5 fields).
                  // Optional but if landlordName is mapped Estatel AI will
                  // match-or-create a Landlord and link it to the
                  // imported Property (de-duplicated by name).
                  { key: 'landlordName',          label: 'Landlord Full Name',        required: false, group: 'landlord' },
                  { key: 'landlordCompanyName',   label: 'Landlord Company Name',     required: false, group: 'landlord' },
                  { key: 'landlordType',          label: 'Landlord Type (Individual / Company)', required: false, group: 'landlord' },
                  { key: 'landlordAddressLine1',  label: 'Landlord Address Line 1',   required: false, group: 'landlord' },
                  { key: 'landlordAddressLine2',  label: 'Landlord Address Line 2',   required: false, group: 'landlord' },
                  { key: 'landlordCity',          label: 'Landlord City',             required: false, group: 'landlord' },
                  { key: 'landlordPostcode',      label: 'Landlord Postcode',         required: false, group: 'landlord' },
                  { key: 'landlordEmail',         label: 'Landlord Email',            required: false, group: 'landlord' },
                  { key: 'landlordPhone',         label: 'Landlord Phone',            required: false, group: 'landlord' },
                ].reduce((acc, field, idx, arr) => {
                  // Inject a group header before the first item of each group.
                  const prevGroup = idx === 0 ? null : arr[idx - 1].group;
                  if (field.group !== prevGroup) {
                    acc.push({ __header: true, group: field.group });
                  }
                  acc.push(field);
                  return acc;
                }, []).map((row) => {
                  if (row.__header) {
                    const labels = { tenant: 'Tenant fields', property: 'Property fields',
                                     tenancy: 'Tenancy fields', landlord: 'Landlord fields' };
                    return (
                      <div key={`hdr-${row.group}`}
                           className="mapping-group-header"
                           data-testid={`mapping-group-${row.group}`}
                           style={{ gridColumn: '1 / -1',
                                    fontSize: '0.78rem',
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                    color: row.group === 'landlord' ? '#0073EA' : '#475569',
                                    padding: '0.6rem 0 0.2rem',
                                    borderBottom: '1px solid #e2e8f0',
                                    margin: '0.4rem 0 0.3rem' }}>
                        {labels[row.group] || row.group}
                      </div>
                    );
                  }
                  const { key, label, group } = row;
                  const cf = mappingConfidence[key];
                  const selectedCol = importData.columnMapping[key] || '';
                  const isUnmapped = !selectedCol;
                  const pillStyle = cf === 'high'
                    ? { background: '#dcfce7', color: '#166534', borderColor: '#86efac' }
                    : cf === 'medium'
                      ? { background: '#fef3c7', color: '#92400e', borderColor: '#fcd34d' }
                      : { background: '#f1f5f9', color: '#475569', borderColor: '#cbd5e1' };
                  const pillLabel = isUnmapped
                    ? 'Not found in upload'
                    : cf === 'high' ? 'High match'
                      : cf === 'medium' ? 'Likely match'
                        : 'Low — please check';
                  return (
                    <div className="mapping-row" key={key} data-group={group}>
                      <label>{label}</label>
                      <select
                        value={selectedCol}
                        onChange={(e) => setImportData(prev => ({
                          ...prev,
                          columnMapping: { ...prev.columnMapping, [key]: e.target.value },
                        }))}
                        data-testid={`map-${key.toLowerCase()}`}
                      >
                        <option value="">Select column…</option>
                        {Object.keys(importData.csvData[0] || {}).map(col => (
                          <option key={col} value={col}>{col}</option>
                        ))}
                      </select>
                      {(cf || isUnmapped) && (
                        <span
                          data-testid={`mapping-confidence-${key.toLowerCase()}`}
                          style={{
                            display: 'inline-block',
                            marginLeft: '0.5rem',
                            padding: '2px 8px',
                            borderRadius: '9999px',
                            fontSize: '0.72rem',
                            fontWeight: 600,
                            border: '1px solid',
                            ...pillStyle,
                          }}
                        >
                          {pillLabel}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>

              {(() => {
                const cm = importData.columnMapping || {};
                // 2026-02 — Per spec: phone is NOT a hard stop. Email
                // and landlord fields are optional. Required: tenant
                // name (first+last), property address, rent. Phone is
                // surfaced as an amber "missing — proceeds anyway"
                // note, not a blocker.
                const REQUIRED = [
                  ['firstName', 'Tenant First Name'],
                  ['lastName',  'Tenant Last Name'],
                  ['address',   'Property Address'],
                  ['rent',      'Rent Amount'],
                ];
                const missing = REQUIRED.filter(([k]) => !((cm[k] || '').trim())).map(([, label]) => label);
                const isReady = missing.length === 0;
                const phoneMissing = !((cm.phone || '').trim());
                const landlordMissing = !((cm.landlordName || '').trim());
                return (
                  <>
                    {!isReady && (
                      <p data-testid="apply-mapping-hint"
                         style={{ color: '#92400e', fontSize: '0.85rem',
                                  marginTop: '0.25rem' }}>
                        Still to map: {missing.join(', ')}
                      </p>
                    )}
                    {isReady && (phoneMissing || landlordMissing) && (
                      <p data-testid="apply-mapping-soft-warning"
                         style={{ color: '#92400e', fontSize: '0.85rem',
                                  marginTop: '0.25rem' }}>
                        Heads-up — not found in upload:{' '}
                        {[phoneMissing && 'Tenant Phone',
                          landlordMissing && 'Landlord Name']
                          .filter(Boolean).join(', ')}.
                        You can proceed and complete these later from the dashboard.
                      </p>
                    )}
                    <button
                      className="btn-primary"
                      onClick={applyColumnMapping}
                      disabled={!isReady}
                      data-testid="apply-mapping"
                    >
                      Apply Mapping & Continue to Review
                    </button>
                  </>
                );
              })()}
            </div>
          )}
        </div>

        {/* Card B: Tenancy Agreements — bulk PDF / image extraction. */}
        <div
          className={`import-path-card ${importData.selectedPath === 'pdf' ? 'is-selected' : ''}`}
          data-testid="card-tenancy-agreements"
        >
          <h3>Upload Tenancy Agreements</h3>
          <p className="card-tagline" style={{ color: '#475569', fontSize: '0.9rem' }}>
            Best if your tenancy data is mainly stored in PDF agreements. Estatel AI AI will read each agreement, extract the landlord, property, tenant, rent and deposit details, and organise everything into the correct hierarchy.
          </p>
          <p style={{ fontSize: '0.8rem', color: '#64748b', margin: '0.4rem 0 0.6rem' }}>
            Accepted: PDF (you can upload many in one go)
          </p>

          <div className="dropzone" data-testid="pdf-dropzone">
            <input
              type="file"
              accept=".pdf,.png,.jpg,.jpeg"
              multiple
              className="file-input"
              onChange={(e) => {
                if (e.target.files.length > 0) {
                  extractPdfs(Array.from(e.target.files));
                  setImportData(prev => ({ ...prev, selectedPath: 'pdf' }));
                }
              }}
            />
            <p>Drag and drop PDF / PNG / JPG files here or click to browse</p>
          </div>

          {extractingPdfs && (
            <p className="info-text" data-testid="pdf-extracting">
              Reading your tenancy agreements…
            </p>
          )}

          {/* 2026-02 — Simplified post-upload UI. Previously this
              rendered a 6-column table (File / Tenant / Property / Rent
              / Landlord / Confidence) which left empty cells when AI
              wasn't 100% confident — looked half-broken. The agent now
              sees just the filename + a confidence pill + a "Review on
              Step 4" hint. The full editable extraction lives on
              Step 4 where they can fix anything they need. */}
          {!extractingPdfs && importData.extractedData.length > 0 && (
            <div className="pdf-extraction-results"
                 data-testid="pdf-extract-results"
                 style={{ marginTop: '0.75rem' }}>
              <p className="info-text" style={{ marginBottom: '0.5rem' }}>
                <strong>{importData.extractedData.length}</strong>{' '}
                file{importData.extractedData.length === 1 ? '' : 's'} read.
                Full details available for review and editing on the next step.
              </p>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0,
                            display: 'grid', gap: '0.4rem' }}>
                {importData.extractedData.map((row, idx) => {
                  const cf = (row.confidence || 'low').toLowerCase();
                  const pillStyle = cf === 'high'
                    ? { background: '#dcfce7', color: '#166534', borderColor: '#86efac' }
                    : cf === 'medium'
                      ? { background: '#fef3c7', color: '#92400e', borderColor: '#fcd34d' }
                      : { background: '#fee2e2', color: '#991b1b', borderColor: '#fca5a5' };
                  const ok = row.ok !== false && (row.firstName || row.address || row.rent);
                  return (
                    <li key={idx}
                        data-testid={`pdf-extract-row-${idx}`}
                        style={{
                          display: 'flex', alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '0.55rem 0.75rem',
                          background: '#f8fafc',
                          border: '1px solid #e2e8f0',
                          borderRadius: 6,
                          gap: '0.75rem',
                        }}>
                      <span style={{ fontWeight: 500, color: '#0f172a',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap' }}>
                        {row.filename}
                      </span>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '2px 10px',
                          borderRadius: '9999px',
                          fontSize: '0.72rem',
                          fontWeight: 600,
                          border: '1px solid',
                          ...pillStyle,
                        }}>
                        {ok
                          ? (cf === 'high' ? 'High confidence'
                              : cf === 'medium' ? 'Medium confidence' : 'Review on Step 4')
                          : 'Review on Step 4'}
                      </span>
                    </li>
                  );
                })}
              </ul>
              <p data-testid="pdf-extract-review-hint"
                 style={{ marginTop: '0.6rem', color: '#475569',
                          fontSize: '0.85rem' }}>
                Click <strong>Next</strong> at the bottom of the page to review and edit
                tenant, property, landlord and tenancy details before saving.
              </p>
            </div>
          )}
        </div>

        {/* Card C: Concierge Onboarding. Hands off the rest of setup to
            the Estatel AI team and drops the agent into the dashboard with
            the amber "Onboarding in progress" banner. */}
        <div
          className="import-path-card import-path-card-concierge"
          data-testid="card-concierge-onboarding"
          style={{ background: '#fffbeb', borderColor: '#fde68a' }}
        >
          <h3 style={{ color: '#92400e' }}>Concierge Onboarding</h3>
          <p className="card-tagline" style={{ color: '#78350f', fontSize: '0.9rem' }}>
            Let our onboarding concierge handle the setup for you. You can enter the dashboard now while your account is being prepared.
          </p>
          <button
            type="button"
            onClick={requestConciergeOnboarding}
            disabled={conciergeSubmitting}
            className="btn-primary"
            data-testid="btn-concierge-card"
            // Lighter amber to contrast better against the soft amber
            // card background. Previously was the much darker #d97706
            // which felt overpowering.
            style={{
              marginTop: '0.6rem',
              background: '#fbbf24',
              borderColor: '#fbbf24',
              color: '#78350f',
              fontWeight: 600,
            }}
          >
            {conciergeSubmitting ? 'Handing off…' : 'Skip Setup — Onboarding Concierge Will Handle This'}
          </button>
        </div>
      </div>
    </div>
  );

  // NOTE: Phase 1 leaves the original 3-card body below intact (renamed)
  // so any other code paths can still import / reference it. Step 3 above
  // is the canonical UI.
  const renderStep3Legacy = () => (
    <div className="wizard-step">
      <h2>Import Your Data</h2>
      <p className="step-description">Choose how you want to import your tenancy data</p>

      <div className="import-paths">
        {/* Path A: CSV */}
        <div className="import-path-card">
          <h3>Path A: CSV from Your CRM</h3>
          <p>Upload a CSV file from your existing system</p>

          <input
            type="file"
            accept=".csv"
            onChange={(e) => {
              if (e.target.files[0]) {
                parseCSV(e.target.files[0]);
                setImportData(prev => ({ ...prev, selectedPath: 'csv' }));
              }
            }}
            className="file-input"
            data-testid="csv-upload"
          />

          {/* FIX 2 — Test CRM export (deliberately mismatched headers) so
              the agent can practise the column-mapping screen end-to-end
              before risking real client data. */}
          <button
            type="button"
            className="btn-link"
            style={{ background: 'none', border: 'none', color: '#0073EA',
                     textDecoration: 'underline', cursor: 'pointer',
                     padding: '0.4rem 0', fontSize: '0.85rem' }}
            onClick={downloadTestCsv}
            data-testid="download-test-csv">
            Download a sample CRM CSV to try the column-mapping
          </button>
          
          {importData.csvData.length > 0 && (
            <div className="column-mapping">
              <h4>Map Your Columns</h4>
              <p>Match your CSV columns to Estatel AI fields</p>
              
              <div className="mapping-grid">
                <div className="mapping-row">
                  <label>First Name *</label>
                  <select
                    value={importData.columnMapping.firstName || ''}
                    onChange={(e) => setImportData(prev => ({
                      ...prev,
                      columnMapping: {...prev.columnMapping, firstName: e.target.value}
                    }))}
                    data-testid="map-firstname"
                  >
                    <option value="">Select column...</option>
                    {Object.keys(importData.csvData[0] || {}).map(col => (
                      <option key={col} value={col}>{col}</option>
                    ))}
                  </select>
                </div>
                
                <div className="mapping-row">
                  <label>Last Name *</label>
                  <select
                    value={importData.columnMapping.lastName || ''}
                    onChange={(e) => setImportData(prev => ({
                      ...prev,
                      columnMapping: {...prev.columnMapping, lastName: e.target.value}
                    }))}
                    data-testid="map-lastname"
                  >
                    <option value="">Select column...</option>
                    {Object.keys(importData.csvData[0] || {}).map(col => (
                      <option key={col} value={col}>{col}</option>
                    ))}
                  </select>
                </div>
                
                <div className="mapping-row">
                  <label>Email</label>
                  <select
                    value={importData.columnMapping.email || ''}
                    onChange={(e) => setImportData(prev => ({
                      ...prev,
                      columnMapping: {...prev.columnMapping, email: e.target.value}
                    }))}
                    data-testid="map-email"
                  >
                    <option value="">Select column...</option>
                    {Object.keys(importData.csvData[0] || {}).map(col => (
                      <option key={col} value={col}>{col}</option>
                    ))}
                  </select>
                </div>
                
                <div className="mapping-row">
                  <label>Phone *</label>
                  <select
                    value={importData.columnMapping.phone || ''}
                    onChange={(e) => setImportData(prev => ({
                      ...prev,
                      columnMapping: {...prev.columnMapping, phone: e.target.value}
                    }))}
                    data-testid="map-phone"
                  >
                    <option value="">Select column...</option>
                    {Object.keys(importData.csvData[0] || {}).map(col => (
                      <option key={col} value={col}>{col}</option>
                    ))}
                  </select>
                </div>
                
                <div className="mapping-row">
                  <label>Property Address *</label>
                  <select
                    value={importData.columnMapping.address || ''}
                    onChange={(e) => setImportData(prev => ({
                      ...prev,
                      columnMapping: {...prev.columnMapping, address: e.target.value}
                    }))}
                    data-testid="map-address"
                  >
                    <option value="">Select column...</option>
                    {Object.keys(importData.csvData[0] || {}).map(col => (
                      <option key={col} value={col}>{col}</option>
                    ))}
                  </select>
                </div>
                
                <div className="mapping-row">
                  <label>Rent Amount *</label>
                  <select
                    value={importData.columnMapping.rent || ''}
                    onChange={(e) => setImportData(prev => ({
                      ...prev,
                      columnMapping: {...prev.columnMapping, rent: e.target.value}
                    }))}
                    data-testid="map-rent"
                  >
                    <option value="">Select column...</option>
                    {Object.keys(importData.csvData[0] || {}).map(col => (
                      <option key={col} value={col}>{col}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              {/* FIX 1 (defensive) — the Apply Mapping button only enables
                  when the five REQUIRED dropdowns are mapped: firstName,
                  lastName, phone, address, rent. Email is optional and
                  must not block. Factored out into a named variable so
                  the validation is trivially inspectable and a clear
                  "Still unmapped: …" hint appears while ghosted. */}
              {(() => {
                const cm = importData.columnMapping || {};
                const REQUIRED = [
                  ['firstName', 'First Name'],
                  ['lastName',  'Last Name'],
                  ['phone',     'Phone'],
                  ['address',   'Property Address'],
                  ['rent',      'Rent Amount'],
                ];
                const missing = REQUIRED.filter(([k]) => !((cm[k] || '').trim())).map(([, label]) => label);
                const isReady = missing.length === 0;
                return (
                  <>
                    {!isReady && (
                      <p data-testid="apply-mapping-hint"
                         style={{ color: '#92400e', fontSize: '0.85rem',
                                  marginTop: '0.25rem' }}>
                        Still to map: {missing.join(', ')}
                      </p>
                    )}
                    <button
                      className="btn-primary"
                      onClick={applyColumnMapping}
                      disabled={!isReady}
                      data-testid="apply-mapping"
                    >
                      Apply Mapping & Continue to Review
                    </button>
                  </>
                );
              })()}
            </div>
          )}
        </div>
        
        {/* Path B: Template */}
        <div className="import-path-card">
          <h3>Path B: Estatel AI Template</h3>
          <p>Download our pre-formatted Excel template</p>

          <button
            type="button"
            className="btn-secondary"
            onClick={downloadImportTemplate}
            data-testid="download-template">
            📥 Download Template
          </button>

          <div className="upload-section">
            <p>After filling it out, upload here:</p>
            <input
              type="file"
              accept=".xlsx,.xls"
              className="file-input"
              onChange={(e) => {
                if (e.target.files[0]) {
                  parseTemplateXlsx(e.target.files[0]);
                }
              }}
              data-testid="template-upload"
            />
          </div>
        </div>
        
        {/* Path C: PDF / scan / image — FIX 3: real Claude-powered
            extraction with OCR for scanned PDFs and PNG/JPG images. */}
        <div className="import-path-card">
          <h3>Path C: Tenancy Agreement Documents</h3>
          <p>Upload PDFs, scans or photos for AI extraction (PDF, PNG, JPG)</p>
          {/* FIX 7 — tenancy agreement PDF upload is optional. Agents
              without digital copies can skip; a reminder will appear on
              the Property Detail Overview tab until they upload later. */}
          <p data-testid="path-c-optional-note"
             style={{ background: '#fff7ed', border: '1px solid #fdba74',
                      color: '#9a3412', padding: '0.55rem 0.85rem',
                      borderRadius: 6, fontSize: '0.85rem',
                      margin: '0.4rem 0 0.85rem' }}>
            Optional — if you have digital copies upload now and they will be
            saved on the property record. If you don't, you can use Path A or
            Path B and upload tenancy agreements later from each property's
            Documents tab.
          </p>

          <div className="dropzone" data-testid="pdf-dropzone">
            <input
              type="file"
              accept=".pdf,.png,.jpg,.jpeg"
              multiple
              className="file-input"
              onChange={(e) => {
                if (e.target.files.length > 0) {
                  extractPdfs(Array.from(e.target.files));
                }
              }}
            />
            <p>📄 Drag and drop PDF / PNG / JPG files here or click to browse</p>
          </div>

          {extractingPdfs && (
            <p className="info-text" data-testid="pdf-extracting">
              Extracting tenant + landlord details from your PDFs…
            </p>
          )}

          {!extractingPdfs && importData.extractedData.length > 0 && (
            <div className="pdf-extraction-results">
              <h4>Extraction Results</h4>
              <p className="info-text">
                Review tenant <strong>and landlord</strong> details below.
                Confirm landlord match on Step 4.
              </p>
              <table className="extraction-table" data-testid="pdf-extract-table">
                <thead>
                  <tr>
                    <th>File</th>
                    <th>Tenant</th>
                    <th>Property</th>
                    <th>Rent</th>
                    <th>Landlord</th>
                    <th>Confidence</th>
                  </tr>
                </thead>
                <tbody>
                  {importData.extractedData.map((row, idx) => {
                    if (!row.ok) {
                      return (
                        <tr key={idx} data-testid={`pdf-extract-error-${idx}`}>
                          <td>{row.filename}</td>
                          <td colSpan="5" className="confidence-low">
                            Extraction failed: {row.error}
                          </td>
                        </tr>
                      );
                    }
                    const t = row.tenant || {};
                    const p = row.property || {};
                    const tn = row.tenancy || {};
                    const l = row.landlord || {};
                    const tenantName = [t.first_name, t.last_name]
                      .filter(Boolean).join(' ') || '—';
                    const propAddr = [p.address_line1, p.city, p.postcode]
                      .filter(Boolean).join(', ') || '—';
                    const rent = tn.rent_amount
                      ? `£${tn.rent_amount} ${tn.rent_frequency || ''}`.trim()
                      : '—';
                    const landlordLabel = (
                      l.full_name && l.company_name
                        ? `${l.full_name} (${l.company_name})`
                        : (l.full_name || l.company_name || '—')
                    );
                    const conf = (row.confidence || 'medium').toLowerCase();
                    const wasOcr = row.route === 'scanned_pdf_ocr'
                                || row.route === 'image_ocr';
                    return (
                      <tr key={idx} data-testid={`pdf-extract-row-${idx}`}>
                        <td>
                          {row.filename}
                          {wasOcr && (
                            <div data-testid={`pdf-extract-ocr-pill-${idx}`}
                                 style={{ marginTop: '0.3rem',
                                          display: 'inline-block',
                                          background: '#fff7ed',
                                          color: '#9a3412',
                                          border: '1px solid #fdba74',
                                          borderRadius: 4,
                                          padding: '0.15rem 0.5rem',
                                          fontSize: '0.75rem',
                                          fontWeight: 500 }}>
                              OCR extracted — please verify carefully
                            </div>
                          )}
                        </td>
                        <td className="confidence-high">{tenantName}</td>
                        <td>{propAddr}</td>
                        <td>{rent}</td>
                        <td data-testid={`pdf-extract-landlord-${idx}`}>
                          {landlordLabel}
                        </td>
                        <td>
                          <span className={`confidence-badge ${conf}`}>
                            {conf.charAt(0).toUpperCase() + conf.slice(1)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
  
  // Path C (FIX 3) — per-extracted-PDF landlord decision. Each entry:
  //   { status: 'pending'|'matched'|'unmatched'|'created'|'skipped',
  //     landlord_id, full_name, company_name }
  // The auto-match runs once when the agent enters Step 4 with PDF data.
  const [pdfLandlordDecisions, setPdfLandlordDecisions] = useState([]);
  useEffect(() => {
    if (currentStep !== 4) return;
    if (!importData.extractedData || importData.extractedData.length === 0) return;
    let cancelled = false;
    (async () => {
      const decisions = await Promise.all(
        importData.extractedData.map(async (row) => {
          if (!row.ok || !row.landlord) return { status: 'no_landlord' };
          const ll = row.landlord;
          if (!ll.full_name && !ll.company_name) return { status: 'no_landlord' };
          const params = new URLSearchParams();
          if (ll.full_name) params.set('name', ll.full_name);
          if (ll.company_name) params.set('company', ll.company_name);
          try {
            const res = await fetch(
              `${API_BASE_URL}/api/onboarding/match-landlord?${params}`,
              { credentials: 'include' });
            // FIX 2 — read as text to dodge "body stream already read".
            const text = await res.text();
            const data = text ? JSON.parse(text) : {};
            if (data.matched) {
              return { status: 'matched',
                       landlord_id: data.landlord_id,
                       full_name: data.full_name,
                       company_name: data.company_name };
            }
            return { status: 'unmatched',
                     full_name: ll.full_name,
                     company_name: ll.company_name };
          } catch {
            return { status: 'unmatched',
                     full_name: ll.full_name,
                     company_name: ll.company_name };
          }
        })
      );
      if (!cancelled) setPdfLandlordDecisions(decisions);
    })();
    return () => { cancelled = true; };
  }, [currentStep, importData.extractedData]);   // eslint-disable-line react-hooks/exhaustive-deps

  const createPdfLandlord = async (idx) => {
    const row = importData.extractedData[idx];
    const ll = row?.landlord;
    if (!ll) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/onboarding/create-landlord-stub`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          full_name: ll.full_name || null,
          company_name: ll.company_name || null,
          address_line1: ll.address_line1 || null,
          address_line2: ll.address_line2 || null,
          city: ll.city || null,
          postcode: ll.postcode || null,
        }),
      });
      // FIX 2 — read as text to dodge "body stream already read".
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); }
      catch { data = { detail: text || 'Invalid response' }; }
      if (res.ok) {
        setPdfLandlordDecisions(prev => prev.map((d, i) =>
          i === idx ? { ...d, status: 'created',
                        landlord_id: data.landlord_id } : d));
      } else {
        window.alert('Could not create landlord: ' + (data.detail || res.status));
      }
    } catch (err) {
      window.alert('Could not create landlord: ' + err.message);
    }
  };

  const skipPdfLandlord = (idx) =>
    setPdfLandlordDecisions(prev => prev.map((d, i) =>
      i === idx ? { ...d, status: 'skipped' } : d));


  const renderStep4 = () => (
    <div className="wizard-step">
      <h2>⚠️ Review and Confirm (MANDATORY)</h2>
      <p className="step-description critical">
        This is your data integrity gate. Review all imported records carefully before they go live.
        <strong> No data will be written to the database until you confirm.</strong>
      </p>
      
      {/* FIX 3 — Path C landlord auto-match-vs-create-new prompt. Renders
          one card per extracted PDF that surfaced a landlord. The agent
          must either accept the auto-match, click 'Create New Landlord',
          or click 'Skip — link later'. Skipped properties surface in the
          'Properties without a linked landlord' dashboard reminder. */}
      {pdfLandlordDecisions.length > 0 && (
        <div className="pdf-landlord-section" data-testid="pdf-landlord-section"
             style={{ background: '#f8fafc', padding: '1rem 1.25rem',
                      border: '1px solid #cbd5e1', borderRadius: 8,
                      marginBottom: '1.5rem' }}>
          <h3 style={{ marginTop: 0 }}>Landlords from Tenancy Agreements</h3>
          <p style={{ color: '#475569', fontSize: '0.9rem' }}>
            For each extracted PDF below, confirm whether the landlord
            already exists in Estatel AI or should be created as a new
            profile. Skipping leaves the property without a linked landlord
            (you can fix this later in <strong>Settings &gt; Landlords</strong>).
          </p>
          {pdfLandlordDecisions.map((d, idx) => {
            const row = importData.extractedData[idx];
            if (!row || !row.ok) return null;
            const ll = row.landlord || {};
            const llLabel = (ll.full_name && ll.company_name)
              ? `${ll.full_name} (${ll.company_name})`
              : (ll.full_name || ll.company_name || '—');
            // FIX 5 — surface extracted email/phone + AI confidence next to
            // the landlord name so the agent can review every field at a
            // glance before linking or creating a new record.
            const confidence = row.confidence;
            const conf = confidence === 'high'
              ? { bg: '#dcfce7', fg: '#166534', txt: 'High confidence' }
              : confidence === 'medium'
                ? { bg: '#fef3c7', fg: '#92400e', txt: 'Medium confidence' }
                : confidence === 'low'
                  ? { bg: '#fee2e2', fg: '#991b1b', txt: 'Low confidence' }
                  : null;
            const tenant = row.tenant || {};
            const property = row.property || {};
            return (
              <div key={idx}
                   data-testid={`pdf-landlord-decision-${idx}`}
                   style={{ padding: '0.85rem 1rem', background: '#fff',
                            border: '1px solid #e2e8f0', borderRadius: 6,
                            marginTop: '0.6rem' }}>
                <div style={{ fontSize: '0.78rem', color: '#64748b',
                              marginBottom: '0.4rem' }}>
                  {row.filename}
                  {conf && (
                    <span data-testid={`pdf-confidence-${idx}`}
                          style={{ marginLeft: '0.6rem',
                                   background: conf.bg, color: conf.fg,
                                   padding: '0.1rem 0.45rem',
                                   borderRadius: 3, fontSize: '0.72rem',
                                   fontWeight: 600 }}>
                      {conf.txt}
                    </span>
                  )}
                </div>
                <div style={{ display: 'grid',
                              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                              gap: '0.65rem', marginBottom: '0.6rem' }}>
                  <div>
                    <div style={{ fontSize: '0.7rem', color: '#94a3b8',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.04em' }}>Landlord</div>
                    <div style={{ fontWeight: 600, color: '#0f172a' }}
                         data-testid={`pdf-landlord-name-${idx}`}>
                      {llLabel}
                    </div>
                    {ll.email && (
                      <div style={{ fontSize: '0.82rem', color: '#475569' }}
                           data-testid={`pdf-landlord-email-${idx}`}>
                        {ll.email}
                      </div>
                    )}
                    {ll.phone_number && (
                      <div style={{ fontSize: '0.82rem', color: '#475569' }}
                           data-testid={`pdf-landlord-phone-${idx}`}>
                        {ll.phone_number}
                      </div>
                    )}
                    {(ll.address_line1 || ll.postcode) && (
                      <div style={{ fontSize: '0.82rem', color: '#475569' }}>
                        {[ll.address_line1, ll.address_line2, ll.city, ll.postcode]
                          .filter(Boolean).join(', ')}
                      </div>
                    )}
                  </div>
                  <div>
                    <div style={{ fontSize: '0.7rem', color: '#94a3b8',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.04em' }}>Tenant</div>
                    <div style={{ fontWeight: 500, color: '#0f172a' }}>
                      {[tenant.first_name, tenant.last_name].filter(Boolean).join(' ') || '—'}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.7rem', color: '#94a3b8',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.04em' }}>Property</div>
                    <div style={{ fontWeight: 500, color: '#0f172a' }}>
                      {[property.address_line1, property.city, property.postcode]
                        .filter(Boolean).join(', ') || '—'}
                    </div>
                  </div>
                </div>
                <div>
                  {d.status === 'matched' && (
                    <span data-testid={`pdf-landlord-status-matched-${idx}`}
                          style={{ background: '#dcfce7', color: '#166534',
                                   padding: '0.3rem 0.6rem', borderRadius: 4,
                                   fontSize: '0.85rem' }}>
                      ✓ Auto-matched to existing landlord
                    </span>
                  )}
                  {d.status === 'created' && (
                    <span data-testid={`pdf-landlord-status-created-${idx}`}
                          style={{ background: '#dbeafe', color: '#1e40af',
                                   padding: '0.3rem 0.6rem', borderRadius: 4,
                                   fontSize: '0.85rem' }}>
                      ✓ New landlord profile created
                    </span>
                  )}
                  {d.status === 'skipped' && (
                    <span data-testid={`pdf-landlord-status-skipped-${idx}`}
                          style={{ background: '#fff7ed', color: '#9a3412',
                                   padding: '0.3rem 0.6rem', borderRadius: 4,
                                   fontSize: '0.85rem' }}>
                      ⚠ Skipped — property left without landlord
                    </span>
                  )}
                  {d.status === 'unmatched' && (
                    <div style={{ display: 'flex', gap: '0.5rem',
                                  flexWrap: 'wrap' }}>
                      <span style={{ alignSelf: 'center', fontSize: '0.85rem',
                                     color: '#475569' }}>
                        Create new landlord profile for this name?
                      </span>
                      <button
                        type="button"
                        onClick={() => createPdfLandlord(idx)}
                        data-testid={`pdf-landlord-create-${idx}`}
                        style={{ background: '#0073EA', color: '#fff',
                                 border: 'none', padding: '0.4rem 0.9rem',
                                 borderRadius: 4, cursor: 'pointer',
                                 fontSize: '0.85rem' }}>
                        Yes — create
                      </button>
                      <button
                        type="button"
                        onClick={() => skipPdfLandlord(idx)}
                        data-testid={`pdf-landlord-skip-${idx}`}
                        style={{ background: '#fff', color: '#475569',
                                 border: '1px solid #cbd5e1',
                                 padding: '0.4rem 0.9rem', borderRadius: 4,
                                 cursor: 'pointer', fontSize: '0.85rem' }}>
                        No — link later
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {reviewData.duplicates.length > 0 && (
        <div className="duplicates-section">
          <h3>🔍 Duplicates Detected</h3>
          <p>The following records have matching phone numbers or emails. You must resolve each duplicate before proceeding.</p>
          
          {reviewData.duplicates.map(dup => (
            <div key={dup.id} className={`duplicate-card ${reviewData.resolvedDuplicates.has(dup.id) ? 'resolved' : ''}`}>
              <div className="duplicate-label">Possible Duplicate</div>
              <div className="duplicate-records">
                {dup.records.map((record, idx) => (
                  <div key={idx} className="duplicate-record">
                    <strong>{record.firstName} {record.lastName}</strong>
                    <p>Phone: {record.phone}</p>
                    <p>Email: {record.email}</p>
                  </div>
                ))}
              </div>
              
              {!reviewData.resolvedDuplicates.has(dup.id) && (
                <div className="duplicate-actions">
                  <button
                    className="btn-secondary btn-sm"
                    onClick={() => resolveDuplicate(dup.id, 'keep_both')}
                    data-testid="keep-both"
                  >
                    Keep Both
                  </button>
                  <button
                    className="btn-danger btn-sm"
                    onClick={() => resolveDuplicate(dup.id, 'delete_one', 0)}
                    data-testid="delete-second"
                  >
                    Delete Second
                  </button>
                  <button
                    className="btn-danger btn-sm"
                    onClick={() => resolveDuplicate(dup.id, 'delete_one', 1)}
                    data-testid="delete-first"
                  >
                    Delete First
                  </button>
                </div>
              )}
              
              {reviewData.resolvedDuplicates.has(dup.id) && (
                <div className="duplicate-resolved">✓ Resolved</div>
              )}
            </div>
          ))}
        </div>
      )}
      
      <div className="review-table-container" ref={reviewTableRef}>
        <table className="review-table">
          <thead>
            <tr>
              <th>First Name *</th>
              <th>Last Name *</th>
              <th>Email</th>
              <th>Phone *</th>
              <th>Property Address *</th>
              <th>Rent *</th>
            </tr>
          </thead>
          <tbody>
            {reviewData.records.map(record => (
              <tr key={record.id}>
                <td>
                  <input
                    type="text"
                    value={record.firstName}
                    onChange={(e) => handleCellEdit(record.id, 'firstName', e.target.value)}
                    className={!record.firstName ? 'required-missing' : ''}
                    data-testid="edit-firstname"
                  />
                </td>
                <td>
                  <input
                    type="text"
                    value={record.lastName}
                    onChange={(e) => handleCellEdit(record.id, 'lastName', e.target.value)}
                    className={!record.lastName ? 'required-missing' : ''}
                    data-testid="edit-lastname"
                  />
                </td>
                <td>
                  <input
                    type="email"
                    value={record.email}
                    onChange={(e) => handleCellEdit(record.id, 'email', e.target.value)}
                    data-testid="edit-email"
                  />
                </td>
                <td>
                  <input
                    type="text"
                    value={record.phone}
                    onChange={(e) => handleCellEdit(record.id, 'phone', e.target.value)}
                    onBlur={(e) => handlePhoneBlur(record.id, e.target.value)}
                    data-testid="edit-phone"
                  />
                </td>
                <td>
                  <input
                    type="text"
                    value={record.address}
                    onChange={(e) => handleCellEdit(record.id, 'address', e.target.value)}
                    className={!record.address ? 'required-missing' : ''}
                    data-testid="edit-address"
                  />
                </td>
                <td>
                  <input
                    type="text"
                    value={record.rent}
                    onChange={(e) => handleCellEdit(record.id, 'rent', e.target.value)}
                    className={!record.rent ? 'required-missing' : ''}
                    data-testid="edit-rent"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {!reviewData.hasScrolledToBottom && reviewData.records.length > 5 && (
        <div className="scroll-reminder">
          ⬇️ Please scroll to the bottom to see all records
        </div>
      )}
      
      <div className="confirm-section">
        <button
          className="btn-primary btn-large"
          onClick={confirmAndGoLive}
          disabled={!validateStep(4)}
          data-testid="confirm-go-live"
        >
          {validateStep(4) ? '✓ Confirm — Make These Records Live' : '⚠️ Resolve Issues to Continue'}
        </button>
        
        {!validateStep(4) && (
          <div className="validation-messages">
            {reviewData.duplicates.some(dup => !reviewData.resolvedDuplicates.has(dup.id)) && (
              <p className="error-message">❌ You have unresolved duplicates</p>
            )}
            {reviewData.records.some(r => !r.firstName || !r.lastName || !r.address || !r.rent) && (
              <p className="error-message">❌ Some required fields are empty (highlighted in red)</p>
            )}
            {!reviewData.hasScrolledToBottom && reviewData.records.length > 5 && (
              <p className="error-message">❌ Please scroll to the bottom to see all records</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
  
  const renderStep5 = () => (
    <div className="wizard-step">
      <h2>Upload Compliance Documents</h2>
      <p className="step-description">Upload compliance documents for your properties (optional - you can do this later)</p>
      
      <div className="compliance-grid">
        {complianceData.properties.map((property, idx) => (
          <div key={idx} className="compliance-property-card">
            <h4>{property.address}</h4>
            
            <div className="compliance-docs-grid">
              {['EPC', 'Gas Safety', 'EICR', 'Miscellaneous'].map(docType => (
                <div key={docType} className="compliance-doc-item">
                  <label>{docType}</label>
                  <div className="doc-status">
                    {property.documents[docType] ? (
                      <span className="status-uploaded">✓ Uploaded</span>
                    ) : (
                      <span className="status-missing">Missing</span>
                    )}
                  </div>
                  <input
                    type="file"
                    accept=".pdf"
                    className="file-input-small"
                    data-testid={`upload-${docType.toLowerCase().replace(' ', '-')}`}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      
      <div className="bulk-upload-section">
        <h3>Or Upload All Documents at Once</h3>
        <div className="dropzone-large">
          <p>📄 Drop all compliance documents here - AI will match them automatically</p>
          <input
            type="file"
            accept=".pdf"
            multiple
            className="file-input"
            data-testid="bulk-compliance-upload"
          />
        </div>
      </div>
    </div>
  );
  
  // ENHANCEMENT 4 — Pre-Tenancy Referencing wizard step. Sits between
  // the compliance upload step (Step 5) and the new Setup-Complete step
  // (now Step 7). Field is optional; the agent can skip and add it later
  // via Settings ▸ Pre-Tenancy. Setup-checklist Item 4 ticks as soon as
  // this URL is saved.
  const [referencingUrl, setReferencingUrl] = useState('');
  const [referencingSaving, setReferencingSaving] = useState(false);
  const [referencingSaved, setReferencingSaved] = useState(false);

  const saveReferencingUrl = async () => {
    setReferencingSaving(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/pre-tenancy/settings`,
        { method: 'PUT', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ referencing_provider_url: referencingUrl.trim() || null }) }
      );
      const text = await res.text();
      if (!res.ok) {
        let detail = '';
        try { detail = JSON.parse(text).detail || ''; } catch { detail = text; }
        window.alert('Could not save referencing URL: ' + detail);
        return;
      }
      setReferencingSaved(true);
      nextStep();
    } finally {
      setReferencingSaving(false);
    }
  };

  // 2026-06-04 — Step 6: Contractors (Part 4 of the spec). Optional —
  // agent can skip and add contractors later. Reuses the dashboard
  // Contractors component embedded inside the wizard step.
  const renderStepContractors = () => (
    <div className="wizard-step" data-testid="step-contractors">
      <h2>Maintenance workflow & contractors</h2>
      <p className="step-description">
        Choose how Estatel AI handles maintenance, then add the contractors
        you trust. Estatel AI will use your roster to contact the right
        people automatically when issues arise.
      </p>

      <OnboardingMaintenanceAutonomy />

      <h3 style={{ margin: '1.6rem 0 0.4rem' }}>Your contractor roster</h3>
      <p style={{ color: '#64748b', fontSize: '0.9rem', margin: '0 0 1rem' }}>
        Adding contractors now means Estatel AI can automatically notify
        them when maintenance issues arise. You can also do this later
        from the Contractors page.
      </p>
      <Contractors embeddedHeading={true} />

      <div style={{ marginTop: '1.2rem' }}>
        <button
          type="button"
          onClick={nextStep}
          data-testid="skip-contractors"
          style={{ background: '#fff', color: '#0073EA',
                   border: '1px solid #0073EA',
                   padding: '0.6rem 1.4rem', borderRadius: 6,
                   cursor: 'pointer' }}>
          Skip — I'll add contractors later
        </button>
      </div>
    </div>
  );

  const renderStep6 = () => (
    <div className="wizard-step" data-testid="step-pre-tenancy-referencing">
      <h2>Set Up Pre-Tenancy Referencing</h2>
      <p className="step-description">
        If you use a referencing provider such as Goodlord, Vouch, or
        HomeLet, paste your applicant referencing link below. Estatel AI
        will use this link when sending referencing invitations to
        prospective tenants.
      </p>

      <div className="form-group">
        <label htmlFor="referencing-provider-url">
          Referencing Provider URL <span style={{ color: '#94a3b8' }}>(optional)</span>
        </label>
        <input
          id="referencing-provider-url"
          type="url"
          value={referencingUrl}
          onChange={(e) => { setReferencingUrl(e.target.value); setReferencingSaved(false); }}
          placeholder="https://your-provider.com/apply"
          data-testid="referencing-provider-url-input"
          style={{ width: '100%', padding: '0.7rem', borderRadius: 6,
                   border: '1px solid #cbd5e1', fontSize: '0.95rem' }}
        />
        <p style={{ color: '#64748b', fontSize: '0.83rem', marginTop: '0.3rem' }}>
          You can also add or change this later in Settings ▸ Pre-Tenancy.
        </p>
        {referencingSaved && (
          <p data-testid="referencing-saved-msg"
             style={{ color: '#166534', fontSize: '0.85rem', marginTop: '0.3rem' }}>
            ✓ Referencing URL saved.
          </p>
        )}
      </div>

      <div style={{ display: 'flex', gap: '0.6rem', marginTop: '1rem',
                    flexWrap: 'wrap' }}>
        <button
          type="button"
          className="btn-primary"
          onClick={saveReferencingUrl}
          disabled={referencingSaving || !referencingUrl.trim()}
          data-testid="save-referencing-url"
          style={{ background: '#0073EA', color: '#fff', border: 'none',
                   padding: '0.6rem 1.4rem', borderRadius: 6,
                   cursor: (referencingSaving || !referencingUrl.trim())
                     ? 'not-allowed' : 'pointer',
                   opacity: (referencingSaving || !referencingUrl.trim()) ? 0.55 : 1 }}>
          {referencingSaving ? 'Saving…' : 'Save & Continue'}
        </button>
        <button
          type="button"
          onClick={nextStep}
          data-testid="skip-referencing-url"
          style={{ background: '#fff', color: '#0073EA',
                   border: '1px solid #0073EA',
                   padding: '0.6rem 1.4rem', borderRadius: 6,
                   cursor: 'pointer' }}>
          Skip — I'll add this later
        </button>
      </div>
    </div>
  );

  // ENHANCEMENT 5 — Demo data loader on the completion screen.
  const [loadingDemo, setLoadingDemo] = useState(false);
  const loadDemoData = async () => {
    setLoadingDemo(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/onboarding/demo-data/load-full`,
        { method: 'POST', credentials: 'include' });
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { data = { detail: text }; }
      if (!res.ok) {
        window.alert('Could not load demo data: ' + (data.detail || res.status));
        return;
      }
      window.alert(data.already_loaded
        ? 'Demo data already loaded.'
        : 'Demo data loaded! Taking you to the dashboard…');
      if (onComplete) onComplete();
      window.location.href = '/';
    } finally {
      setLoadingDemo(false);
    }
  };

  const renderStep7 = () => (
    <div className="wizard-step">
      <div className="completion-header">
        <div className="completion-icon">🎉</div>
        <h2>Setup Complete!</h2>
        <p className="step-description">Your agency is now ready to use Estatel AI</p>
      </div>
      
      <div className="summary-stats">
        <div className="summary-stat-card">
          <div className="stat-number">{summary.tenantsImported}</div>
          <div className="stat-label">Tenants Imported</div>
        </div>
        <div className="summary-stat-card">
          <div className="stat-number">{summary.propertiesImported}</div>
          <div className="stat-label">Properties Imported</div>
        </div>
        <div className="summary-stat-card">
          <div className="stat-number">{summary.complianceDocsUploaded}</div>
          <div className="stat-label">Compliance Docs Uploaded</div>
        </div>
      </div>
      
      {summary.outstandingItems.length > 0 && (
        <div className="outstanding-items">
          <h3>⚠️ Outstanding Items</h3>
          <p>These items need your attention but don't block you from getting started:</p>
          <ul className="outstanding-list">
            {summary.outstandingItems.map((item, idx) => (
              <li key={idx} className="outstanding-item">{item}</li>
            ))}
          </ul>
        </div>
      )}

      {/* 2026-05-29 — Compliance Scanner traffic-light report */}
      {scanningCompliance && (
        <div data-testid="compliance-scanning"
             style={{ padding: '1rem', background: '#f8fafc',
                      border: '1px solid #e2e8f0', borderRadius: 8,
                      margin: '1rem 0', color: '#475569' }}>
          Running compliance health check on your imported properties…
        </div>
      )}
      {!scanningCompliance && complianceScans && (
        <ComplianceScannerReport
          scans={complianceScans}
          summary={complianceSummary}
          onRefresh={refreshComplianceScans}
          title="Compliance Health Report"
          subtitle="We scanned every property against the RRA 2026 compliance checklist. Resolve critical items before going live, or acknowledge them and proceed."
        />
      )}
      
      <div className="completion-actions"
           style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap',
                    alignItems: 'center', marginTop: '1.5rem' }}>
        <button
          className="btn-primary btn-large"
          onClick={() => {
            logAuditTrail(7, 'completed onboarding');
            if (onComplete) onComplete();
            window.location.href = '/';
          }}
          data-testid="go-to-dashboard"
          style={{ background: '#0073EA', color: '#fff', border: 'none' }}
        >
          Go to Dashboard →
        </button>
        <button
          type="button"
          onClick={loadDemoData}
          disabled={loadingDemo}
          data-testid="load-demo-data"
          style={{ background: '#fff', color: '#0073EA',
                   border: '1px solid #0073EA',
                   padding: '0.85rem 1.5rem', borderRadius: 6,
                   fontWeight: 500, fontSize: '0.95rem',
                   cursor: loadingDemo ? 'not-allowed' : 'pointer',
                   opacity: loadingDemo ? 0.6 : 1 }}>
          {loadingDemo ? 'Loading…' : 'Load Demo Data — Explore Estatel AI with sample data'}
        </button>
      </div>
    </div>
  );
  
  return (
    <div className="onboarding-container">
      <div className="onboarding-header">
        <h1>Estatel AI Setup Wizard</h1>
        {renderStepIndicator()}
      </div>
      
      <div className="onboarding-content">
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}
        {currentStep === 4 && renderStep4()}
        {currentStep === 5 && renderStep5()}
        {currentStep === 6 && renderStepContractors()}
        {currentStep === 7 && renderStep6()}
        {currentStep === 8 && renderStep7()}
      </div>
      
      {currentStep < 8 && (
        <div className="onboarding-navigation">
          <button
            className="btn-secondary"
            onClick={prevStep}
            disabled={currentStep === 1}
          >
            ← Previous
          </button>

          {/* 2026-02 — Persistent concierge skip. Available from Step 2
              onwards (Step 1 must be complete first so the concierge has
              the agency's contact details). Click → POST
              /api/agency/concierge-request → AgentClient is flagged and
              the agent is dropped into the dashboard with the amber
              "Onboarding in progress" banner. */}
          {currentStep >= 2 && (
            <button
              type="button"
              onClick={requestConciergeOnboarding}
              disabled={conciergeSubmitting}
              data-testid="btn-concierge-skip"
              style={{
                // 2026-02 — Softer amber so the contrast against the
                // wizard's white background isn't too aggressive.
                background: '#fffbeb',
                color: '#92400e',
                border: '1px solid #fde68a',
                padding: '0.55rem 1.1rem',
                borderRadius: 6,
                fontWeight: 500,
                fontSize: '0.9rem',
                cursor: conciergeSubmitting ? 'not-allowed' : 'pointer',
              }}
            >
              {conciergeSubmitting
                ? 'Handing off…'
                : 'Skip Setup — Onboarding Concierge Will Handle This'}
            </button>
          )}

          {currentStep !== 4 && currentStep !== 7 && (
            <button
              className="btn-primary"
              onClick={nextStep}
              disabled={!validateStep(currentStep) || agencySaving}
              data-testid="next-step"
            >
              {agencySaving && currentStep === 1 ? 'Saving…' : 'Next →'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default Onboarding;


// 2026-06-04 — Embedded in Onboarding step 6. Lets the agent pick the
// maintenance autonomy mode (managed_by_estateiq vs managed_by_agent)
// BEFORE they see the contractor roster. Same shape as the Settings
// tab; persisted to the same endpoint.
function OnboardingMaintenanceAutonomy() {
  const [mode, setMode] = useState('managed_by_estateiq');
  const [highHours, setHighHours] = useState(2);
  const [mediumHours, setMediumHours] = useState(24);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/maintenance/settings`,
          { credentials: 'include' });
        if (res.ok) {
          const d = await res.json();
          setMode(d.maintenance_autonomy || 'managed_by_estateiq');
          setHighHours(d.high_priority_escalation_hours || 2);
          setMediumHours(d.medium_priority_escalation_hours || 24);
        }
      } catch (_e) { /* non-fatal */ }
    })();
  }, []);

  const persist = async (overrides = {}) => {
    setSaving(true); setSaved(false);
    try {
      const body = {
        maintenance_autonomy: overrides.mode ?? mode,
        high_priority_escalation_hours: Number(overrides.highHours ?? highHours) || 2,
        medium_priority_escalation_hours: Number(overrides.mediumHours ?? mediumHours) || 24,
      };
      const res = await fetch(`${API_BASE_URL}/api/maintenance/settings`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) setSaved(true);
    } finally { setSaving(false); }
  };

  const pick = (id) => {
    setMode(id); setSaved(false);
    persist({ mode: id });
  };

  const Card = (id, title, body) => (
    <label key={id}
           data-testid={`onb-maint-mode-${id}`}
           style={{ display: 'block',
                    border: mode === id ? '2px solid #ea580c' : '1px solid #e2e8f0',
                    background: mode === id ? '#fff7ed' : '#fff',
                    borderRadius: 12, padding: '0.9rem 1rem',
                    marginBottom: 10, cursor: 'pointer' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <input type="radio" name="onb-maint-mode" checked={mode === id}
               onChange={() => pick(id)} style={{ marginTop: 4 }} />
        <div>
          <strong style={{ color: '#0f172a' }}>{title}</strong>
          <p style={{ margin: '4px 0 0', color: '#475569', fontSize: '0.88rem' }}>{body}</p>
        </div>
      </div>
    </label>
  );

  return (
    <div data-testid="onb-maint-autonomy"
         style={{ background: '#fff7ed', border: '1px solid #fed7aa',
                  borderRadius: 14, padding: '1rem 1.2rem', marginTop: 12 }}>
      <h3 style={{ margin: '0 0 0.4rem', color: '#9a3412' }}>
        How should Estatel AI handle maintenance?
      </h3>
      {Card('managed_by_estateiq', 'Managed by Estatel AI',
        'When a tenant reports an issue we automatically email them an acknowledgement, raise the ticket, and contact the right contractor (handyman for low/medium, specialists for high/emergency) — all from your connected mailbox.')}
      {Card('managed_by_agent', 'Managed by Agent',
        'Estatel AI still logs the issue and acknowledges the tenant, but does NOT email any contractor. The ticket sits in your queue for you to handle.')}

      {mode === 'managed_by_estateiq' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 8 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontWeight: 600, color: '#9a3412', fontSize: '0.85rem' }}>
              High priority — escalate after (hrs)
            </span>
            <input type="number" min={1} max={168} value={highHours}
                   data-testid="onb-maint-high-hours"
                   onChange={e => { setHighHours(e.target.value); setSaved(false); }}
                   onBlur={() => persist({})}
                   style={{ padding: '0.45rem 0.6rem', border: '1px solid #fed7aa',
                            borderRadius: 8, fontSize: '0.9rem' }} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontWeight: 600, color: '#9a3412', fontSize: '0.85rem' }}>
              Medium priority — prompt after (hrs)
            </span>
            <input type="number" min={1} max={168} value={mediumHours}
                   data-testid="onb-maint-medium-hours"
                   onChange={e => { setMediumHours(e.target.value); setSaved(false); }}
                   onBlur={() => persist({})}
                   style={{ padding: '0.45rem 0.6rem', border: '1px solid #fed7aa',
                            borderRadius: 8, fontSize: '0.9rem' }} />
          </label>
        </div>
      )}
      <div style={{ marginTop: 6, fontSize: '0.8rem',
                    color: saving ? '#9a3412' : (saved ? '#166534' : '#9a3412') }}>
        {saving ? 'Saving…' : (saved ? 'Saved' : 'Auto-saves when you change a value')}
      </div>
    </div>
  );
}
